import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, inrPlain } from "@/lib/api";
import { toast } from "sonner";
import { CardSkeleton } from "@/components/Skeletons";
import { Trash2, Pencil, Calendar, User as UserIcon } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { useGroup } from "@/context/GroupContext";

export default function ExpenseDetails() {
  const { slug, expenseId } = useParams();
  const [expense, setExpense] = useState(null);
  const [category, setCategory] = useState(null);
  const { user } = useAuth();
  const { groupId, members } = useGroup();
  const navigate = useNavigate();

  const membersMap = React.useMemo(
    () => Object.fromEntries((members || []).map((u) => [u.id, u])),
    [members]
  );

  useEffect(() => {
    async function load() {
      const { data } = await api.get(`/expenses/${expenseId}`);
      setExpense(data);
      if (groupId) {
        const cats = await api.get("/categories", { params: { group_id: groupId } });
        setCategory(cats.data.find((c) => c.id === data.category_id) || null);
      }
    }
    load();
  }, [expenseId, groupId]);

  const del = async () => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success("Deleted");
      navigate(`/${slug}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!expense) return <CardSkeleton />;

  const payer = membersMap[expense.paid_by];
  const canModify = expense.created_by === user.id;

  return (
    <div className="space-y-5 lg:max-w-2xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Expense</div>
        <h1 className="heading text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 mt-1">{expense.title}</h1>
      </div>

      <div className="rounded-3xl p-6 bg-white border border-slate-200 space-y-3">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Amount</div>
        <div className="amount text-4xl font-bold text-slate-900" data-testid="expense-detail-amount">{inrPlain(expense.amount)}</div>
        <div className="text-sm text-slate-500">Each share <b className="text-slate-900">{inrPlain(expense.share_amount)}</b></div>
        {category && (
          <div className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
            <span className="w-2 h-2 rounded-full" style={{ background: category.color }} />
            {category.name}
          </div>
        )}
      </div>

      <div className="rounded-3xl p-5 bg-white border border-slate-200 space-y-3">
        <div className="flex items-center gap-3">
          <UserIcon className="w-4 h-4 text-slate-400" />
          <div className="text-sm">
            <div className="text-slate-500">Paid by</div>
            <div className="font-semibold text-slate-900">{payer?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-slate-400" />
          <div className="text-sm">
            <div className="text-slate-500">Date</div>
            <div className="font-semibold text-slate-900">
              {format(new Date(expense.expense_date || expense.created_at), "PPP")}
            </div>
          </div>
        </div>
        {expense.description && (
          <div className="pt-2 text-sm">
            <div className="text-slate-500">Note</div>
            <div className="text-slate-800 mt-1">{expense.description}</div>
          </div>
        )}
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Split with</div>
        <div className="space-y-2">
          {expense.included_member_ids?.map((mid) => {
            const m = membersMap[mid];
            return (
              <div key={mid} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200">
                {m?.photo && <img src={m.photo} alt="" className="w-9 h-9 rounded-full object-cover" />}
                <div className="flex-1 font-semibold text-slate-900 text-sm">{m?.name}</div>
                <div className="amount font-bold text-slate-700 text-sm">{inrPlain(expense.share_amount)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {expense.receipt && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Receipt</div>
          <img src={expense.receipt} alt="receipt" className="w-full rounded-2xl border border-slate-200" />
        </div>
      )}

      {canModify && (
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/${slug}/expenses/${expenseId}/edit`)}
            className="flex-1 h-12 rounded-full bg-slate-100 hover:bg-slate-200 font-semibold text-slate-700 flex items-center justify-center gap-2"
            data-testid="expense-edit-button"
          >
            <Pencil className="w-4 h-4" /> Edit
          </button>
          <button
            onClick={del}
            className="flex-1 h-12 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold flex items-center justify-center gap-2"
            data-testid="expense-delete-button"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}
